
import {
  cloneData, cloneList, DataModel, DataModelBuilder, isSameData,
  isSameList, SerializationContext, SerializedData,
} from '../dto/dto.js';
import { toEnumFromString, fromEnumAsString } from '../../serialization/enum-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { BusinessModuleRef } from './business-module-ref.js';
import dayjs from 'dayjs';
import { VersionRange } from '../core/version-range.js';

export enum OrderCompletionTaskStatus {
  Pending = 'PENDING',
  Completed = 'COMPLETED',
}

export class OrderCompletionTask extends DataModel {
  static readonly TYPE_ID: string = '4a6bacdba6844f34906dba5b94a16a0e';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly uniqueId: string,
    public readonly schedulerTaskId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly status: OrderCompletionTaskStatus,
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string | null,
    public readonly completionDate: dayjs.Dayjs | null,
    public readonly requestIds: string[],
  ) {
    super();
  }

  get $typeId(): string {
    return OrderCompletionTask.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(OrderCompletionTask.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    schedulerTaskId: string,
    creationDate: dayjs.Dayjs,
    status: OrderCompletionTaskStatus,
    businessRef: BusinessModuleRef,
    eventId: string | null,
    completionDate: dayjs.Dayjs | null,
    requestIds: string[],
  }): OrderCompletionTask {
    return new OrderCompletionTask(
        args.uniqueId,
        args.schedulerTaskId,
        args.creationDate,
        args.status,
        args.businessRef,
        args.eventId,
        args.completionDate,
        args.requestIds,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OrderCompletionTask {
    return new OrderCompletionTask(
        MapUtils.getString(map, 'uniqueId'),
        MapUtils.getString(map, 'schedulerTaskId'),
        context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
        toEnumFromString(MapUtils.getString(map, '$status'), OrderCompletionTaskStatus),
        BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
        MapUtils.getStringOrNull(map, 'eventId'),
        context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'completionDate')),
        MapUtils.getList<string>(map, 'requestIds', (v) => v as string),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'schedulerTaskId': this.schedulerTaskId,
      'creationDate': context.encodeDate(this.creationDate),
      '$status': fromEnumAsString(this.status),
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'completionDate': context.encodeDateOrNull(this.completionDate),
      'requestCount': this.requestIds.length,
      'requestIds': this.requestIds,
    };
  }

  clone(): this {
    return new OrderCompletionTask(
        this.uniqueId,
        this.schedulerTaskId,
        TimeZone.cloneDate(this.creationDate),
        this.status,
        cloneData(this.businessRef),
        this.eventId,
        TimeZone.cloneDateOrNull(this.completionDate),
        cloneList(this.requestIds),
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.schedulerTaskId === this.schedulerTaskId &&
        other.creationDate === this.creationDate &&
        other.status === this.status &&
        isSameData(other.businessRef, this.businessRef) &&
        other.eventId === this.eventId &&
        other.completionDate === this.completionDate &&
        isSameList(other.requestIds, this.requestIds);
  }

  copyAsComplete(completionDate: dayjs.Dayjs): this {
    return new OrderCompletionTask(
        this.uniqueId,
        this.schedulerTaskId,
        TimeZone.cloneDate(this.creationDate),
        OrderCompletionTaskStatus.Completed,
        cloneData(this.businessRef),
        this.eventId,
        completionDate,
        cloneList(this.requestIds),
    ) as this;
  }
}

export class OrderCompletionTaskBuilder extends DataModelBuilder<OrderCompletionTask> {
  build(context: SerializationContext, map: SerializedData): OrderCompletionTask {
    return OrderCompletionTask.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OrderCompletionTask.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OrderCompletionTask.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `eventId` was made nullable
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: OrderCompletionTask,
      map: SerializedData
  ): SerializedData {
    const versionRange = MapUtils.getVersionRange(map);
    let version = versionRange.from;

    if(version >= 2) {
      if(model.eventId == null) return map;

      version = 1;
      map['$schema_version'] = VersionRange.encodedBetween(1, 2);
    }

    return map;
  }
}
